import { Box, Skeleton, Typography, useMediaQuery } from "@mui/material";
import { FC, useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";
import { InsureAssistPerformanceStyles } from "./InsureAssistPerformanceStyles";
import { useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";

interface IAverageLatencyUsage {
  latencyUsageData: any;
}

const AverageLatencyUsage: FC<IAverageLatencyUsage> = ({
  latencyUsageData,
}) => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const { isLoading } = useAppSelector((store: RootState) => store.common);
  const formattedData = useMemo(() => {
    return latencyUsageData?.map((item: any) => ({
      date: dayjs(item.date).format("MM/DD HH:mm"),
      "Latency Used": item.latency,
    }));
  }, [latencyUsageData]);

  return (
    <Box 
      sx={InsureAssistPerformanceStyles.averageTokenUsageContainer}
      id="average_latency_usage_container"
      data-test-id="average_latency_usage_container"
      aria-label="Average Latency Usage Chart"
    >
      <Typography 
        sx={{ fontWeight: "600", marginBottom: 2 }}
        id="average_latency_usage_title"
        data-test-id="average_latency_usage_title"
        aria-label="Average Latency Usage"
      >
        Average Latency Usage
      </Typography>
      {isLoading ? (
        <Skeleton
          variant="rounded"
          height="250px"
          animation="wave"
          sx={{
            width: isMobile ? "100%" : "auto",
            minWidth: isMobile ? "100%" : "466px",
            flex: 1,
          }}
          id="average_latency_usage_skeleton"
          data-test-id="average_latency_usage_skeleton"
          aria-label="Loading Latency Usage Chart"
        />
      ) : (
        <Box 
          sx={{ height: 300 }}
          id="average_latency_usage_chart_container"
          data-test-id="average_latency_usage_chart_container"
          aria-label="Latency Usage Chart"
        >
          {!latencyUsageData?.length ? (
            <Box 
              sx={InsureAssistPerformanceStyles.noDataContainer}
              id="average_latency_usage_no_data"
              data-test-id="average_latency_usage_no_data"
              aria-label="No Latency Usage Data Available"
            >
              <Typography
                sx={{ textAlign: "center", fontSize: 16, color: "black" }}
                id="average_latency_usage_no_data_text"
                data-test-id="average_latency_usage_no_data_text"
                aria-label="No records found"
              >
                No records found
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer 
              width="100%" 
              height="100%"
              id="average_latency_usage_chart"
              data-test-id="average_latency_usage_chart"
              aria-label="Latency Usage Line Chart"
            >
              <LineChart data={formattedData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" dy={10} />
                <YAxis
                  tickFormatter={(value) => (value / 1000).toFixed(2)}
                  label={{
                    value: "Latency (in seconds)",
                    angle: -90,
                    position: "insideLeft",
                    offset: 1,
                    dy: 50,
                  }}
                />
                <Tooltip formatter={(value: number) => `${value} ms`} />
                <Line
                  type="monotone"
                  dataKey="Latency Used"
                  stroke="black"
                  strokeWidth={1}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AverageLatencyUsage;
